import { createEntityId } from '@/core/EntityId';
import type { Vec3 } from '@/flora/FloraInstance';
import {
  defaultPose,
  poseAttachments,
  type FaunaAttachment,
  type FaunaDefinition,
} from './FaunaDefinition';

/**
 * A placed creature or figure — a project entity in its own right, mirroring `FloraInstance`. Plain
 * serializable data, independent of its {@link FaunaDefinition}.
 *
 * Deliberately thinner than a flora instance, and the missing fields are the informative part. There
 * is no `maturity`: a rabbit does not grow over the life of a design, and the one thing that would
 * make it look different at two sizes — a fawn beside a doe — is a separate record, not a slider on
 * this one. There is no `seasonalOffsetDays` either; a placed creature is a moment, not a calendar.
 *
 * What it does carry beyond position and rotation is `poseId`, because pose is the thing about
 * staffage that a designer actually chooses, and `sizeVariation`, so a group of six goldfinches is
 * not six copies of one bird.
 */
export interface FaunaInstance {
  id: string;
  faunaDefinitionId: string;
  /** Ground position. `y` is an offset above the sampled surface — see {@link attachment}. */
  position: Vec3;
  /** Rotation about +Y in radians. Creatures do not tilt; a single angle is the whole of it. */
  rotationY: number;
  /** Which of the definition's poses this one is in. */
  poseId: string;
  /**
   * How this instance meets the world. Stored rather than derived, because a record that allows both
   * `ground` and `perch` has no single right answer and the placement gesture is what decided it.
   */
  attachment: FaunaAttachment;
  /**
   * Height above the surface, in meters. Zero for `ground`; the perch's height for `perch`; the
   * definition's `hoverHeightMeters` — or a value the designer dragged to — for `air`.
   */
  heightOffsetMeters: number;
  /** Signed fraction applied to the definition's size, within its `sizeVariation` band. */
  sizeVariation: number;
  /** Drives any per-instance randomisation in the model, exactly as `FloraInstance.visualSeed` does. */
  visualSeed: number;
  /** Set when the instance was placed as part of a flock or family, so the group moves together. */
  groupId?: string;
  locked: boolean;
  hidden: boolean;
}

export interface CreateFaunaInstanceOptions {
  position?: Vec3;
  rotationY?: number;
  poseId?: string;
  attachment?: FaunaAttachment;
  heightOffsetMeters?: number;
  sizeVariation?: number;
  visualSeed?: number;
  overrides?: Partial<FaunaInstance>;
}

/**
 * Create a placed instance of a definition, with pose and attachment resolved against what the
 * record actually allows.
 *
 * A requested pose or attachment the record does not declare falls back to its default rather than
 * throwing: the caller is usually a placement gesture, and a dropped bird is a better failure than a
 * dropped click. Callers that need to know may compare the result.
 */
export function createFaunaInstance(
  definition: FaunaDefinition,
  options: CreateFaunaInstanceOptions = {},
): FaunaInstance {
  const pose =
    definition.poses.find((candidate) => candidate.id === options.poseId) ?? defaultPose(definition);

  const allowed = poseAttachments(definition, pose);
  const attachment =
    options.attachment && allowed.includes(options.attachment)
      ? options.attachment
      : (allowed[0] as FaunaAttachment);

  const instance: FaunaInstance = {
    id: createEntityId('fauna-'),
    faunaDefinitionId: definition.id,
    position: options.position ?? { x: 0, y: 0, z: 0 },
    rotationY: options.rotationY ?? 0,
    poseId: pose.id,
    attachment,
    heightOffsetMeters:
      options.heightOffsetMeters ?? defaultHeightOffset(definition, attachment),
    sizeVariation: clampVariation(options.sizeVariation ?? 0, definition),
    visualSeed: options.visualSeed ?? Math.floor(Math.random() * 0x7fffffff),
    locked: false,
    hidden: false,
    ...options.overrides,
  };
  return instance;
}

/** Where this attachment sits by default: on the surface, or at the record's hover height. */
export function defaultHeightOffset(
  definition: FaunaDefinition,
  attachment: FaunaAttachment,
): number {
  if (attachment === 'air') return definition.hoverHeightMeters ?? 0;
  return 0;
}

/** Clamp a variation into the record's own band, so an instance can never exceed what it declares. */
export function clampVariation(value: number, definition: FaunaDefinition): number {
  const band = definition.sizeVariation ?? 0;
  if (!Number.isFinite(value)) return 0;
  return Math.min(Math.max(value, -band), band);
}

/** The multiplier this instance applies to its definition's authored size. */
export function instanceScale(instance: FaunaInstance): number {
  return 1 + instance.sizeVariation;
}

/** Deep clone as a fully independent entity with a fresh id — used by Duplicate. */
export function cloneFaunaInstanceIndependent(source: FaunaInstance): FaunaInstance {
  const clone: FaunaInstance = { ...source, id: createEntityId('fauna-'), position: { ...source.position } };
  // A duplicate stands alone: it must not remain a member of the flock it was copied from.
  delete clone.groupId;
  return clone;
}
